import { Browser } from '@capacitor/browser'
import PaymentCloudPayments from '~/components/payment/PaymentCloudPayments.vue'
import { PlanPeriodUnit, type Plan } from '~/models/subscriptions'
import { useAuth } from '~/stores/auth'
import { useNotifications } from '~/stores/notifications'
import { useOrders } from '~/stores/orders'
import { useSubscriptions } from '~/stores/subscriptions'

const cloudpaymentsIntervalMap: Record<PlanPeriodUnit, 'Day' | 'Month'> = {
  [PlanPeriodUnit.Day]: 'Day',
  [PlanPeriodUnit.Month]: 'Month',
}

export const useSubscriptionPurchase = () => {
  const authStore = useAuth()
  const subscriptionsStore = useSubscriptions()
  const { t } = useI18n()
  const { pushNotification } = useNotifications()
  const { resetPaymentsList } = useOrders()

  const cloudPaymentsComponent = ref<null | InstanceType<
    typeof PaymentCloudPayments
  >>(null)

  const isPaymentDataLoading = ref(false)
  const isNavigated = ref(false)

  const onPaymentClose = () => {
    isNavigated.value = false
    isPaymentDataLoading.value = false
  }

  const onPaymentDone = async () => {
    await authStore.refreshProfile()
    onPaymentClose()
  }

  const processCloudPayments = async (plan: Plan, invoiceId: string) => {
    const planPrice = plan.prices?.[0]

    if (!planPrice) {
      return
    }

    await cloudPaymentsComponent.value?.processCloudPayments(
      {
        amount: planPrice.amount,
        currency: planPrice.currency,
        invoiceId,
        description: 'Лицензия на использование онлайн ПО WN Hub',
        receiptLabel: 'Лицензия на использование онлайн ПО WN Hub',
      },
      undefined,
      {
        interval: cloudpaymentsIntervalMap[plan.period_unit],
        period: plan.period,
      }
    )
  }

  const submitPlan = async (plan?: Plan) => {
    if (!authStore.auth) {
      authStore.openLoginModal()
      return
    }

    if (isPaymentDataLoading.value || !plan) {
      return
    }

    const planPrice = plan.prices?.[0]
    const isApp = useDetect().isApp

    isPaymentDataLoading.value = true

    try {
      const paymentData = await subscriptionsStore.buySubscription(
        plan.id,
        planPrice?.currency
      )

      if (paymentData.redirect_url) {
        if (isApp) {
          await Browser.open({ url: paymentData.redirect_url })
        } else {
          window.location.href = paymentData.redirect_url
          isNavigated.value = true
        }
      } else if (paymentData.sub_id) {
        isNavigated.value = true
        await processCloudPayments(plan, paymentData.sub_id)
      } else if (paymentData.activated) {
        await onPaymentDone()
      }

      resetPaymentsList()
    } catch (error) {
      pushNotification({
        title: t('notifications.subscriptions.subscribe_error'),
        theme: 'destructive',
      })

      useLogError(error)
    } finally {
      if (!isNavigated.value) {
        isPaymentDataLoading.value = false
      }
    }
  }

  return {
    cloudPaymentsComponent,
    isPaymentDataLoading,
    onPaymentClose,
    onPaymentDone,
    submitPlan,
  }
}
